import { useState, useEffect, useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import type { WidgetConfiguration, ChatAssistant } from '../types'
import { DEFAULT_WIDGET_CONFIG as DEFAULTS } from '../types'

export function useWidgetConfig(kbId: string, chatAssistant?: ChatAssistant) {
  const queryClient = useQueryClient()
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfiguration | null>(null)
  const [savedConfig, setSavedConfig] = useState<WidgetConfiguration | null>(null)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Build initial configuration from ChatAssistant data
  const buildConfigFromAssistant = useCallback((assistant: ChatAssistant): WidgetConfiguration => {
    const widgetSettings = assistant.widget_settings

    return {
      chat_id: assistant.shared_id || assistant.id,  // Use shared_id for proxy URL (hex hash)
      shared_id: assistant.shared_id || '',
      authentication_token: assistant.authentication_token || '',
      
      chat_title: assistant.chat_title || DEFAULTS.chat_title!,
      assistant_name: assistant.name || DEFAULTS.assistant_name!,
      assistant_subtitle: widgetSettings?.header_subtitle || DEFAULTS.assistant_subtitle!,
      
      header_color: assistant.header_color || DEFAULTS.header_color!,
      chat_button_color: assistant.widget_button_color || DEFAULTS.chat_button_color!,
      send_button_color: assistant.header_color || DEFAULTS.send_button_color!,
      assistant_chat_bubble_color: assistant.assistant_chat_bubble_color || DEFAULTS.assistant_chat_bubble_color!,
      assistant_chat_font_color: assistant.assistant_chat_font_color || DEFAULTS.assistant_chat_font_color!,
      profile_chat_bubble_color: assistant.profile_chat_bubble_color || DEFAULTS.profile_chat_bubble_color!,
      profile_chat_font_color: assistant.profile_chat_font_color || DEFAULTS.profile_chat_font_color!,
      
      show_avatar: assistant.show_avatar ?? DEFAULTS.show_avatar!,
      assistant_avatar_url: assistant.assistant_avatar_url || DEFAULTS.assistant_avatar_url!,
      user_avatar_url: assistant.user_avatar_url || DEFAULTS.user_avatar_url!,
      profile_icon_url: assistant.profile_icon_url || DEFAULTS.profile_icon_url!,
      
      privacy_policy: {
        enabled: widgetSettings?.privacy_policy_enabled ?? DEFAULTS.privacy_policy!.enabled,
        text_prefix: widgetSettings?.privacy_policy_text_prefix || DEFAULTS.privacy_policy!.text_prefix,
        link_text: widgetSettings?.privacy_policy_link_text || DEFAULTS.privacy_policy!.link_text,
        text_suffix: widgetSettings?.privacy_policy_text_suffix || DEFAULTS.privacy_policy!.text_suffix,
        content_type: 'url',
        markdown_content: '',
        page_url: widgetSettings?.privacy_policy_page_url || '',
        popup_title: 'Privacy Policy',
      },
      
      features: DEFAULTS.features!,
      
      agent: assistant.agent ?? DEFAULTS.agent!,
      api_url: window.location.origin,
      feedback_api_url: window.location.origin,
      proxy_url: window.location.origin,
    }
  }, [])

  // Initialize config when chatAssistant is loaded
  useEffect(() => {
    if (chatAssistant) {
      console.log('[useWidgetConfig] Received chatAssistant:', {
        id: chatAssistant.id,
        shared_id: chatAssistant.shared_id,
        name: chatAssistant.name,
      })
      const config = buildConfigFromAssistant(chatAssistant)
      console.log('[useWidgetConfig] Built config with shared_id:', config.shared_id)
      setWidgetConfig(config)
      setSavedConfig(config)
    }
  }, [chatAssistant, buildConfigFromAssistant])

  // Check for unsaved changes
  useEffect(() => {
    if (widgetConfig && savedConfig) {
      const hasChanges = JSON.stringify(widgetConfig) !== JSON.stringify(savedConfig)
      setHasUnsavedChanges(hasChanges)
    }
  }, [widgetConfig, savedConfig])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (config: WidgetConfiguration) => {
      const response = await knowledgeApi.post(
        `/knowledge/${kbId}/save-widget-settings/`,
        config
      )
      return response.data
    },
    onSuccess: () => {
      if (widgetConfig) {
        setSavedConfig(widgetConfig)
        setHasUnsavedChanges(false)
        // Invalidate KB detail query to refresh chat_assistant data
        queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
      }
    },
  })

  const updateConfig = useCallback((updates: Partial<WidgetConfiguration>) => {
    setWidgetConfig((prev) => prev ? { ...prev, ...updates } : null)
  }, [])

  const resetToDefaults = useCallback(() => {
    if (chatAssistant) {
      const defaultConfig = buildConfigFromAssistant(chatAssistant)
      setWidgetConfig({
        ...defaultConfig,
        ...DEFAULTS,
        chat_id: defaultConfig.chat_id,
        shared_id: defaultConfig.shared_id,
        authentication_token: defaultConfig.authentication_token,
        api_url: defaultConfig.api_url,
        feedback_api_url: defaultConfig.feedback_api_url,
        proxy_url: defaultConfig.proxy_url,
      })
    }
  }, [chatAssistant, buildConfigFromAssistant])

  const saveConfig = useCallback(() => {
    if (widgetConfig) {
      saveMutation.mutate(widgetConfig)
    }
  }, [widgetConfig, saveMutation])

  return {
    widgetConfig,
    savedConfig,
    hasUnsavedChanges,
    updateConfig,
    resetToDefaults,
    saveConfig,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
  }
}
